import React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import AppBar from '@mui/material/AppBar';
import { TableFooter } from '@mui/material';
import moment from 'moment';
import ErrorBoundary from '../ErrorBoundary';
import { hasPermission } from 'app/main/loadz/utils';
import { useTranslation } from 'react-i18next';
import './InvoiceReport.css';
import {
  REPORT_TYPES,
  customerName,
  formatInvoiceDate,
  formatMoney,
  getReportTypeTitleKey,
  instanceCustomerName,
  invoiceMaterialWithTax,
  jobLabel,
  shouldShowInvoiceLines,
  sortCustomers,
  sortInvoices,
} from './salesReportUtils';

let ReportData = {};
let startDate = '';
let endDate = '';
let hasDate = false;
let complogo = '';
let groupByCustomer = false;
let groupByJob = false;
let comp_id = '';
let reportType = REPORT_TYPES.DETAIL;

const createData = (
  company_id,
  fullReportData,
  has_date,
  start_date,
  end_date,
  compLogo,
  usergroupByCustomer,
  usergroupByJob,
  userReportType
) => {
  hasDate = has_date;
  startDate = start_date;
  endDate = end_date;
  ReportData = fullReportData || {};
  complogo = compLogo;
  groupByCustomer = usergroupByCustomer;
  groupByJob = usergroupByJob;
  comp_id = company_id;
  reportType = userReportType || REPORT_TYPES.DETAIL;
};

const cellSx = {
  color: 'white',
  padding: '8px',
  textAlign: 'center',
  whiteSpace: 'nowrap',
};

const bodyCellSx = {
  padding: '8px',
  border: 0.5,
  textAlign: 'center',
  whiteSpace: 'nowrap',
};

const InvoiceLineRow = ({ instance, compact, showCustomer, showCommission }) => {
  const invoice = instance.invoice || {};
  return (
    <TableRow style={{ pageBreakInside: 'avoid' }}>
      <TableCell align='center' sx={bodyCellSx}>
        {formatInvoiceDate(invoice)}
      </TableCell>
      {showCustomer && (
        <TableCell align='center' sx={bodyCellSx}>
          {instanceCustomerName(instance)}
        </TableCell>
      )}
      <TableCell align='center' sx={bodyCellSx}>
        {invoice.invoice_no}
      </TableCell>
      <TableCell align='center' sx={bodyCellSx}>
        {comp_id ? `${comp_id}-${invoice.job_no}` : invoice.job_no}
      </TableCell>
      {!compact && (
        <>
          <TableCell align='center' sx={bodyCellSx}>
            {formatMoney(invoice.unit_package_value)}
          </TableCell>
          <TableCell align='center' sx={bodyCellSx}>
            {formatMoney(invoice.waittime_package_value)}
          </TableCell>
          <TableCell align='center' sx={bodyCellSx}>
            {formatMoney(invoiceMaterialWithTax(invoice))}
          </TableCell>
        </>
      )}
      <TableCell align='center' sx={bodyCellSx}>
        {invoice.status}
      </TableCell>
      {showCommission && !compact && (
        <TableCell align='center' sx={bodyCellSx}>
          {formatMoney(instance.agent_comm)}
        </TableCell>
      )}
      <TableCell align='center' sx={{ ...bodyCellSx, fontWeight: 600 }}>
        {formatMoney(invoice.invoice_total_amount)}
      </TableCell>
    </TableRow>
  );
};

const TotalsRow = ({ label, totals, compact, showCustomer, showCommission, colSpanLabel = 3 }) => (
  <TableRow style={{ pageBreakInside: 'avoid', backgroundColor: '#f4f1e6' }}>
    <TableCell align='left' sx={{ ...bodyCellSx, fontWeight: 700, textAlign: 'left' }} colSpan={showCustomer ? colSpanLabel + 1 : colSpanLabel}>
      {label}
    </TableCell>
    {!compact && (
      <>
        <TableCell align='center' sx={{ ...bodyCellSx, fontWeight: 700 }}>
          {formatMoney(totals.total_hauling)}
        </TableCell>
        <TableCell align='center' sx={{ ...bodyCellSx, fontWeight: 700 }}>
          {formatMoney(totals.total_waittime)}
        </TableCell>
        <TableCell align='center' sx={{ ...bodyCellSx, fontWeight: 700 }}>
          {formatMoney((totals.total_material || 0) + (totals.total_material_tax || 0))}
        </TableCell>
      </>
    )}
    <TableCell align='center' sx={{ ...bodyCellSx, fontWeight: 700 }} />
    {showCommission && !compact && (
      <TableCell align='center' sx={{ ...bodyCellSx, fontWeight: 700 }}>
        {formatMoney(totals.total_agent_commission)}
      </TableCell>
    )}
    <TableCell align='center' sx={{ ...bodyCellSx, fontWeight: 700 }}>
      {formatMoney(totals.total_invoice_amount)}
    </TableCell>
  </TableRow>
);

const SummaryRow = ({ label, totals, showCommission }) => (
  <TableRow style={{ pageBreakInside: 'avoid' }}>
    <TableCell align='left' sx={{ ...bodyCellSx, textAlign: 'left' }}>
      {label}
    </TableCell>
    <TableCell align='center' sx={bodyCellSx}>
      {totals.invoice_count}
    </TableCell>
    <TableCell align='center' sx={bodyCellSx}>
      {formatMoney(totals.total_hauling)}
    </TableCell>
    <TableCell align='center' sx={bodyCellSx}>
      {formatMoney(totals.total_waittime)}
    </TableCell>
    <TableCell align='center' sx={bodyCellSx}>
      {formatMoney((totals.total_material || 0) + (totals.total_material_tax || 0))}
    </TableCell>
    {showCommission && (
      <TableCell align='center' sx={bodyCellSx}>
        {formatMoney(totals.total_agent_commission)}
      </TableCell>
    )}
    <TableCell align='center' sx={{ ...bodyCellSx, fontWeight: 600 }}>
      {formatMoney(totals.total_invoice_amount)}
    </TableCell>
  </TableRow>
);

const InvoiceTableHead = ({ compact, showCustomer, showCommission, t }) => (
  <TableHead sx={{ border: 2.5, padding: 'none', margin: 'none' }}>
    <TableRow style={{ backgroundColor: '#2c2e35' }}>
      <TableCell sx={cellSx}>{t('DATE')}</TableCell>
      {showCustomer && <TableCell sx={cellSx}>{t('CUSTOMER')}</TableCell>}
      <TableCell sx={cellSx}>{t('INVOICE_NO')}</TableCell>
      <TableCell sx={cellSx}>{t('JOB_NO')}</TableCell>
      {!compact && (
        <>
          <TableCell sx={cellSx}>{t('HAULING')}</TableCell>
          <TableCell sx={cellSx}>{t('WAIT_TIME')}</TableCell>
          <TableCell sx={cellSx}>{t('MATERIAL_W_TAX')}</TableCell>
        </>
      )}
      <TableCell sx={cellSx}>{t('STATUS')}</TableCell>
      {showCommission && !compact && <TableCell sx={cellSx}>{t('AGENT_COMMISSION')}</TableCell>}
      <TableCell sx={cellSx}>{t('GRAND_TOTAL')}</TableCell>
    </TableRow>
  </TableHead>
);

const InvoiceTable = () => {
  const { t } = useTranslation('navigation');
  const showCommission = hasPermission('invoice.read');
  const showLines = shouldShowInvoiceLines(reportType, groupByCustomer);
  const compact = reportType === REPORT_TYPES.INVOICE;
  const customers = ReportData.customers || {};
  const invoices = sortInvoices(ReportData.invoices || []);
  const customerIds = sortCustomers(customers);
  const grouped = groupByCustomer || groupByJob;
  const titleKey = getReportTypeTitleKey(reportType);
  const dateLabel = hasDate
    ? `${moment(startDate).format('MM/DD/YY')} - ${moment(endDate).format('MM/DD/YY')}`
    : t('ALL_DATES');

  const grandTotals = {
    invoice_count: ReportData.total_invoice_count || invoices.length,
    total_hauling: ReportData.total_hauling || 0,
    total_material: ReportData.total_material || 0,
    total_material_tax: ReportData.total_material_tax || 0,
    total_waittime: ReportData.total_waittime || 0,
    total_agent_commission: ReportData.total_agent_commission || 0,
    total_invoice_amount: ReportData.total_invoice_amount || 0,
  };

  return (
    <div className='flex flex-col w-full' style={{ minWidth: '684px' }}>
      {complogo ? (
        <div className='flex flex-row items-end justify-end'>
          <img width='200px' alt='company logo' src={complogo} />
        </div>
      ) : null}

      <div className='flex flex-col items-start w-full' style={{ breakBefore: 'avoid' }}>
        <Typography fontSize={22} fontWeight='bold' color='black'>
          {t(titleKey)}
        </Typography>
        <Typography fontSize={16} fontWeight='bold' color='black'>
          {dateLabel}
        </Typography>
        <Typography fontSize={13} color='black'>
          {t('INVOICE_COUNT')}: {grandTotals.invoice_count}
        </Typography>
      </div>

      {!showLines ? (
        <Box sx={{ marginTop: 1, width: '100%' }}>
          <Table
            sx={{
              [`& .${tableCellClasses.root}`]: {
                fontSize: 14,
                borderBottom: 2,
                paddingTop: 0,
                paddingBottom: 0,
                paddingRight: 0,
              },
            }}
          >
            <TableHead sx={{ border: 2.5 }}>
              <TableRow style={{ backgroundColor: '#2c2e35' }}>
                <TableCell sx={{ ...cellSx, textAlign: 'left' }}>{t('CUSTOMER')}</TableCell>
                <TableCell sx={cellSx}>{t('INVOICE_COUNT')}</TableCell>
                <TableCell sx={cellSx}>{t('HAULING')}</TableCell>
                <TableCell sx={cellSx}>{t('WAIT_TIME')}</TableCell>
                <TableCell sx={cellSx}>{t('MATERIAL_W_TAX')}</TableCell>
                {showCommission && <TableCell sx={cellSx}>{t('AGENT_COMMISSION')}</TableCell>}
                <TableCell sx={cellSx}>{t('GRAND_TOTAL')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ border: 2.5 }}>
              {customerIds.map((customerId) => {
                const customer = customers[customerId];
                if (groupByJob) {
                  return Object.keys(customer.jobs || {}).map((jobId) => (
                    <SummaryRow
                      key={`${customerId}-${jobId}`}
                      label={`${customerName(customer)} / ${jobLabel(customer.jobs[jobId].job_info, comp_id)}`}
                      totals={customer.jobs[jobId]}
                      showCommission={showCommission}
                    />
                  ));
                }
                return (
                  <SummaryRow
                    key={customerId}
                    label={customerName(customer)}
                    totals={customer}
                    showCommission={showCommission}
                  />
                );
              })}
              <SummaryRow label={t('GRAND_TOTAL')} totals={grandTotals} showCommission={showCommission} />
            </TableBody>
          </Table>
        </Box>
      ) : grouped ? (
        customerIds.map((customerId) => {
          const customer = customers[customerId];
          return (
            <div className='flex flex-col w-full' key={customerId} style={{ marginTop: 12 }}>
              {groupByCustomer && (
                <div className='flex flex-row justify-start w-full' style={{ breakBefore: 'avoid' }}>
                  <Typography fontSize={20} fontWeight='bold' color='black'>
                    {customerName(customer)}
                  </Typography>
                </div>
              )}

              {groupByCustomer && (
                <div className='flex flex-row justify-between w-full flex-wrap' style={{ breakBefore: 'avoid' }}>
                  <Typography fontSize={14} fontWeight='bold' color='black'>
                    {t('HAULING')}: {formatMoney(customer.total_hauling)}
                  </Typography>
                  <Typography fontSize={14} fontWeight='bold' color='black'>
                    {t('MATERIAL')}: {formatMoney(customer.total_material)}
                  </Typography>
                  <Typography fontSize={14} fontWeight='bold' color='black'>
                    {t('MATERIAL_TAX')}: {formatMoney(customer.total_material_tax)}
                  </Typography>
                  <Typography fontSize={14} fontWeight='bold' color='black'>
                    {t('WAIT_TIME')}: {formatMoney(customer.total_waittime)}
                  </Typography>
                  {showCommission && (
                    <Typography fontSize={14} fontWeight='bold' color='black'>
                      {t('AGENT_COMMISSION')}: {formatMoney(customer.total_agent_commission)}
                    </Typography>
                  )}
                </div>
              )}

              <Box style={{ breakBefore: 'avoid', width: '100%' }} sx={{ marginTop: 1 }}>
                {groupByJob ? (
                  <>
                    {Object.keys(customer.jobs || {}).map((jobId) => {
                      const currentJob = customer.jobs[jobId];
                      return (
                        <Box style={{ pageBreakInside: 'avoid' }} sx={{ marginBottom: 1 }} key={jobId}>
                          <AppBar
                            className='flex flex-col items-center justify-between'
                            position='static'
                            elevation={0}
                            sx={{ border: 2, width: '100%', backgroundColor: 'white', color: 'black' }}
                          >
                            <Typography fontWeight='bold' fontSize={15} color='black'>
                              {jobLabel(currentJob.job_info, comp_id)}
                            </Typography>
                            <div className='flex flex-row w-full items-center justify-evenly flex-wrap'>
                              <Typography fontWeight='bold' fontSize={13} color='black'>
                                {t('HAULING')}: {formatMoney(currentJob.total_hauling)}
                              </Typography>
                              <Typography fontWeight='bold' fontSize={13} color='black'>
                                {t('MATERIAL')}: {formatMoney(currentJob.total_material)}
                              </Typography>
                              <Typography fontWeight='bold' fontSize={13} color='black'>
                                {t('WAIT_TIME')}: {formatMoney(currentJob.total_waittime)}
                              </Typography>
                              {showCommission && (
                                <Typography fontWeight='bold' fontSize={13} color='black'>
                                  {t('AGENT_COMMISSION')}: {formatMoney(currentJob.total_agent_commission)}
                                </Typography>
                              )}
                            </div>
                          </AppBar>
                          <Table
                            sx={{
                              [`& .${tableCellClasses.root}`]: {
                                fontSize: 14,
                                borderBottom: 2,
                                paddingTop: 0,
                                paddingBottom: 0,
                                paddingRight: 0,
                              },
                            }}
                          >
                            <InvoiceTableHead
                              compact={compact}
                              showCustomer={false}
                              showCommission={showCommission}
                              t={t}
                            />
                            <TableBody sx={{ border: 2.5 }}>
                              {sortInvoices(currentJob.invoices || []).map((instance, i) => (
                                <InvoiceLineRow
                                  key={`${jobId}-${i}`}
                                  instance={instance}
                                  compact={compact}
                                  showCustomer={false}
                                  showCommission={showCommission}
                                />
                              ))}
                              <TotalsRow
                                label={`${t('JOB')} ${t('TOTAL')}`}
                                totals={currentJob}
                                compact={compact}
                                showCustomer={false}
                                showCommission={showCommission}
                                colSpanLabel={3}
                              />
                            </TableBody>
                            <TableFooter sx={{ border: 0 }}>
                              <TableRow sx={{ border: 0 }}>
                                <TableCell sx={{ border: 0 }} colSpan={8} />
                              </TableRow>
                            </TableFooter>
                          </Table>
                        </Box>
                      );
                    })}
                    {groupByCustomer && (
                      <Box sx={{ border: '2px solid #2c2e35', padding: '6px 10px', marginBottom: 2 }}>
                        <Typography fontWeight='bold' fontSize={15} color='black'>
                          {t('CUSTOMER_TOTAL')} — {formatMoney(customer.total_invoice_amount)}
                        </Typography>
                      </Box>
                    )}
                  </>
                ) : (
                    <Table
                      sx={{
                        [`& .${tableCellClasses.root}`]: {
                          fontSize: 14,
                          borderBottom: 2,
                          paddingTop: 0,
                          paddingBottom: 0,
                          paddingRight: 0,
                        },
                      }}
                    >
                      <InvoiceTableHead
                        compact={compact}
                        showCustomer={false}
                        showCommission={showCommission}
                        t={t}
                      />
                      <TableBody sx={{ border: 2.5 }}>
                        {sortInvoices(customer.invoices || []).map((instance, i) => (
                          <InvoiceLineRow
                            key={i}
                            instance={instance}
                            compact={compact}
                            showCustomer={false}
                            showCommission={showCommission}
                          />
                        ))}
                        {groupByCustomer && (
                          <TotalsRow
                            label={`${t('CUSTOMER_TOTAL')} — ${formatMoney(customer.total_invoice_amount)}`}
                            totals={customer}
                            compact={compact}
                            showCustomer={false}
                            showCommission={showCommission}
                            colSpanLabel={3}
                          />
                        )}
                      </TableBody>
                    </Table>
                  )}
              </Box>
            </div>
          );
        })
      ) : (
        <Box sx={{ marginTop: 1, width: '100%' }}>
          <Table
            sx={{
              [`& .${tableCellClasses.root}`]: {
                fontSize: 14,
                borderBottom: 2,
                paddingTop: 0,
                paddingBottom: 0,
                paddingRight: 0,
              },
            }}
          >
            <InvoiceTableHead compact={compact} showCustomer showCommission={showCommission} t={t} />
            <TableBody sx={{ border: 2.5 }}>
              {invoices.map((instance, i) => (
                <InvoiceLineRow
                  key={i}
                  instance={instance}
                  compact={compact}
                  showCustomer
                  showCommission={showCommission}
                />
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {showLines && (
        <Box sx={{ marginTop: 2, width: '100%', border: '2px solid #2c2e35', padding: '8px 12px' }}>
          <div className='flex flex-row justify-between flex-wrap'>
            <Typography fontSize={16} fontWeight='bold' color='black'>
              {t('GRAND_TOTAL')}
            </Typography>
            <Typography fontSize={14} fontWeight='bold' color='black'>
              {t('INVOICE_COUNT')}: {grandTotals.invoice_count}
            </Typography>
            <Typography fontSize={14} fontWeight='bold' color='black'>
              {t('HAULING')}: {formatMoney(grandTotals.total_hauling)}
            </Typography>
            <Typography fontSize={14} fontWeight='bold' color='black'>
              {t('WAIT_TIME')}: {formatMoney(grandTotals.total_waittime)}
            </Typography>
            <Typography fontSize={14} fontWeight='bold' color='black'>
              {t('MATERIAL_W_TAX')}: {formatMoney((grandTotals.total_material || 0) + (grandTotals.total_material_tax || 0))}
            </Typography>
            {showCommission && (
              <Typography fontSize={14} fontWeight='bold' color='black'>
                {t('AGENT_COMMISSION')}: {formatMoney(grandTotals.total_agent_commission)}
              </Typography>
            )}
            <Typography fontSize={16} fontWeight='bold' color='black'>
              {formatMoney(grandTotals.total_invoice_amount)}
            </Typography>
          </div>
        </Box>
      )}
    </div>
  );
};

const UserTable = () => {
  const { t } = useTranslation('navigation');
  return (
    <div>
      <ErrorBoundary>
        <InvoiceTable />
      </ErrorBoundary>
      <div className='footer align-center items-center justify-center'>
        <Typography fontSize={15} sx={{ marginTop: 2, marginRight: 1 }}>
          {t('POWERED_BY')}
        </Typography>
        <img style={{ width: 80, height: 20, marginTop: 7 }} src='assets/images/logos/loadzlogo.png' alt='Loadz' />
      </div>
    </div>
  );
};

export default class InvoiceReport extends React.Component {
  constructor(props) {
    super(props);
  }

  render() {
    ReportData = {};
    if (this.props) {
      createData(
        this.props.company_id,
        this.props.fullReportData,
        this.props.has_date,
        this.props.start_date,
        this.props.end_date,
        this.props.compLogo,
        this.props.usergroupByCustomer,
        this.props.usergroupByJob,
        this.props.reportType
      );

      return <UserTable />;
    }
  }
}
